import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Pencil, Share2 } from 'lucide-react';
import { mediaUrl } from '../../../utils/mediaUrl';
import { eventNameFor } from '../../../utils/eventName';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getCountryOptions } from '../../../utils/countries';
import { MIN_INTERESTS } from '../../../constants/doctorProfile';
import ProfileEditModal from './ProfileEditModal';
import RecommendedStudies from './RecommendedStudies';
import ProposedChanges from './ProposedChanges';
import ShareEngagementModal from '../share/ShareEngagementModal';
import SpeakerArea, { hasSpeakerSpots } from '../hub/SpeakerArea';
import * as SelfApi from '../selfApi';
import failureText from './failureText';

const API = process.env.REACT_APP_API_URL || '';
const BLUE = '#47ABDB';

// The Hero/Grey layout the other member screens share (selfUi.js copies the
// same values) - the same avatar, the same photo-over-the-hero overlap, the
// same card style. Below the hero, this page uses a grid (see GreyInner).
const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;

const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;
// Below 859px, PhotoWrap pulls the avatar up over the Hero by a fixed amount
// (see its own comment) - on desktop the 15rem min-height below keeps plenty
// of clearance under the headline text no matter what, but that min-height
// is dropped on a phone since the headline block would otherwise leave
// awkward empty space. Extra bottom padding (rather than a min-height, which
// could still be too short on a narrow phone where the tagline wraps to an
// extra line) reserves that same clearance regardless of how many lines the
// headline text wraps to - without it the pulled-up avatar overlaps the
// subheadline/tagline text. Verified at 316-390px: a fixed ~40px gap between
// the tagline and the avatar holds across that whole range.
const HeroInner = styled.div`
  max-width:78rem; margin:0 auto; padding:3.75rem 2.5rem 3.25rem; min-height:15rem;
  @media(max-width:600px){ padding:2.5rem 1.5rem 7.5rem; min-height:0; }
`;
const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2rem,4.4vw,3.4rem);`;
const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;
// max-width keeps the tagline out of the avatar's overlap zone (the photo is
// pulled up over the hero's bottom - see PhotoWrap) so its last wrapped line
// can't run underneath the profile picture.
const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);max-width:40rem;`;

const Grey = styled.div`background:#d0e6ef;`;
// Named grid areas instead of two flex columns: topLeft (banners +
// motivation) and topRight (photo) share row 1 (an empty speaker row 2
// collapses for a plain attendee), bottomLeft
// (RecommendedStudies) and bottomRight (the history: participations that need
// no managing) share the last row. A CSS grid row is exactly as tall as its
// tallest cell, so that row always starts at the same y in both columns no
// matter how tall topLeft's banners get that day - no more fixed min-height
// calibration needed to keep "HEARTPORT EXPERT" and "HEARTS Conferences"
// aligned. align-items:start keeps the photo pinned to the top of row 1 too,
// so it never drifts down when a banner appears above it.
// $withSpeaker (a person asked to a spot): the speaker area - everything about
// speaking, open invitations included - is row 2 on the left, and the history
// starts right under the photo instead of below the speaker area (it spans rows
// 2 and 3; the last row is the flexible one, so a long history grows that row
// and leaves no gap under the speaker area).
const GreyInner = styled.div`
  position:relative; max-width:78rem; margin:0 auto; padding:2rem 2.5rem;
  display:grid; align-items:start; column-gap:2.75rem; row-gap:1.75rem;
  grid-template-columns:1fr 27rem;
  grid-template-areas:"topLeft topRight" "speaker topRight" "bottomLeft bottomRight";
  ${p => (p.$withSpeaker ? 'grid-template-areas:"topLeft topRight" "speaker bottomRight" "bottomLeft bottomRight";grid-template-rows:auto auto 1fr;' : '')}
  @media(max-width:859px){
    grid-template-columns:1fr; grid-template-rows:none; gap:1.5rem; padding:1.5rem;
    grid-template-areas:"topRight" "speaker" "bottomRight" "topLeft" "bottomLeft";
  }
`;
const TopLeft = styled.div`grid-area:topLeft;min-width:0;@media(min-width:860px){padding-top:1.25rem;}`;
const TopRight = styled.div`grid-area:topRight;`;
const BottomLeft = styled.div`grid-area:bottomLeft;min-width:0;`;
const BottomRight = styled.aside`grid-area:bottomRight;`;
// The speaker area ("Referentenbereich", hub/SpeakerArea.js) - the former Speaker Hub page, now part of
// the profile so a speaker has nothing more to click. The anchor is where /speaker/dashboard (the
// links in already sent emails) and the history's "Manage" links land; scroll-margin keeps the
// heading clear of the top edge when it is scrolled to.
export const SPEAKER_AREA_ID = 'referentenbereich';
const SpeakerSection = styled.section`grid-area:speaker;min-width:0;scroll-margin-top:1rem;`;

const MotivationLabel = styled.div`
  display:inline-block;font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;
  padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;
`;
const MotivationText = styled.p`font-size:.92rem;font-style:italic;color:#334155;margin:.3rem 0 0;line-height:1.5;max-width:34rem;`;

const PhotoWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:-130px;
  @media(max-width:859px){ margin-top:-104px; }
`;
const PhotoButton = styled.button`position:relative;border:0;background:none;padding:0;cursor:pointer;border-radius:50%;&:hover .edit-badge{transform:scale(1.08);}`;
const Photo = styled.img`width:190px;height:190px;border-radius:50%;object-fit:cover;border:5px solid ${BLUE};background:#eef2f7;`;
const Initials = styled.div`width:190px;height:190px;border-radius:50%;border:5px solid ${BLUE};background:#eef2f7;display:flex;align-items:center;justify-content:center;font-size:3.2rem;font-weight:800;color:${BLUE};`;
const EditBadge = styled.span`position:absolute;bottom:8px;right:8px;width:34px;height:34px;border-radius:50%;background:${BLUE};color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(15,23,42,.3);border:2px solid #fff;transition:transform .2s;`;
const Name = styled.div`margin-top:.75rem;font-weight:800;font-size:1.2rem;color:#111D4E;`;
const TitleLine = styled.div`color:#5B5B5B;font-size:.9rem;margin-top:.15rem;word-break:break-word;`;

const Nudge = styled.div`background:#eff6ff;border:1px solid #bfdbfe;color:#1e3a8a;padding:.85rem 1rem;margin-bottom:1.5rem;font-size:.9rem;display:flex;flex-wrap:wrap;gap:.6rem;align-items:center;justify-content:space-between;`;
const NudgeLink = styled.button`background:none;border:0;padding:0;color:#1e3a8a;font-weight:700;text-decoration:underline;cursor:pointer;font-size:.9rem;`;

// Same plain headline treatment as RecommendedStudies.js's own section
// title (bold caps + italic subtitle, no background box), so the two
// sections in this two-column layout read as one visual system.
const HistoryHeader = styled.div`margin:0 0 1rem;`;
const HistoryTitle = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;`;
const HistorySubtitle = styled.div`color:#5B5B5B;font-weight:400;font-style:italic;font-size:.92rem;margin-top:.2rem;`;
// $past fades the whole card (thumbnail included) so a glance down the list
// separates what's already happened from what's still upcoming, without
// touching the real outcome status (Confirmed/Declined/...) next to it -
// see PastTag below for that distinction.
const RegCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);${p => (p.$past ? 'opacity:.6;' : '')}${p => (p.$highlighted ? `outline:2px solid ${BLUE};outline-offset:2px;` : '')}`;
const CardTop = styled.div`display:flex;gap:1rem;align-items:center;`;
const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;`;
const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;`;
const RegInfo = styled.div`min-width:0;`;
const RegName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;
const RegMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;
const STATUS_COLORS = {
  CONFIRMED: ['#dcfce7', '#166534'], ACCEPTED: ['#dcfce7', '#166534'],
  DECLINED: ['#fee2e2', '#991b1b'], REJECTED: ['#fee2e2', '#991b1b'], CANCELLED: ['#fee2e2', '#991b1b'],
  WITHDRAWN: ['#f1f5f9', '#64748b'],
};
const StatusTag = styled.span`display:inline-block;margin-top:.35rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:${p => (STATUS_COLORS[p.$status] || ['#e0f2fe'])[0]};color:${p => (STATUS_COLORS[p.$status] || [null, '#075985'])[1]};`;
const RoleTag = styled.span`display:inline-block;padding:.1rem .5rem;font-size:.68rem;font-weight:800;letter-spacing:.03em;text-transform:uppercase;background:#111D4E;color:#fff;margin-right:.4rem;`;
const PastTag = styled.span`display:inline-block;margin-top:.35rem;margin-left:.4rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:#e5e7eb;color:#6b7280;`;
const RegSummary = styled.div`color:#334155;font-size:.85rem;margin-top:.3rem;font-style:italic;`;
const RegLink = styled(Link)`display:inline-block;margin-top:.4rem;color:#0369a1;font-size:.8rem;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
// The speaker-side rows' "Manage" link: the speaker area is on this same page.
const RegAnchor = styled.a`display:inline-block;margin-top:.4rem;color:#0369a1;font-size:.8rem;font-weight:600;text-decoration:none;&:hover{text-decoration:underline;}`;
// Share icon for a confirmed speaking/moderating/paneling entry - the
// speaker area's own ShareButton style (hub/SpeakerArea.js), where a talk is
// managed.
const ShareIconButton = styled.button`display:inline-flex;align-items:center;justify-content:center;width:1.9rem;height:1.9rem;flex:none;margin-left:auto;border:1px solid #cbd5e1;background:#fff;color:#334155;cursor:pointer;transition:border-color .15s,color .15s;&:hover{border-color:${BLUE};color:${BLUE};}`;
const ThumbLink = styled(Link)`display:block;flex:none;`;
const EventNameLink = styled(Link)`color:inherit;text-decoration:none;&:hover{text-decoration:underline;}`;
const Empty = styled.p`color:#5B6B78;`;

// Event thumbnail with graceful fallback (missing URL or image that fails to load) - the same as hub/SpeakerArea.js's EventThumb.
const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

const VerifyNotice = styled.div`background:#fef3c7;color:#92400e;padding:.85rem 1rem;margin-bottom:1.5rem;font-size:.9rem;display:flex;flex-wrap:wrap;gap:.6rem;align-items:center;justify-content:space-between;`;
const VerifyLink = styled.button`background:none;border:0;padding:0;color:#92400e;font-weight:700;text-decoration:underline;cursor:pointer;font-size:.9rem;`;

const RegisteredBanner = styled.div`background-color:#d1fae5;color:#065f46;padding:1rem;margin-bottom:1.5rem;text-align:center;`;
const Message = styled.p`font-size:.875rem;color:${p => (p.error ? '#b91c1c' : '#065f46')};`;
// The same plain info box the invite screens use, for the two ways a login's own registration can
// fall short (the email is not confirmed yet, the person is already registered).
const Notice = styled.div`background:#eff6ff;border:1px solid #bfdbfe;color:#1e3a8a;padding:.85rem 1rem;margin-bottom:1.5rem;font-size:.9rem;line-height:1.5;`;

// PUT /api/me/profile treats every key in the request as an explicit edit, so
// the form sends only what actually moved while it was open. Nothing is lost
// by leaving a field out: a field the person did not touch keeps its value
// either way, and an untouched field is never written back over a newer value.
const isPastDate = (v) => Boolean(v) && new Date(v) < new Date();

const changedFields = (original, current) => {
  const payload = {};
  Object.keys(current || {}).forEach((key) => {
    if (JSON.stringify(current[key]) !== JSON.stringify(original?.[key])) payload[key] = current[key];
  });
  return payload;
};

const AttendeeProfile = () => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Set by LoginForm.js when landing here right after a login that also
  // completed an event registration (see registerForEvent in authUtils.js) -
  // the only path where that registration would otherwise finish with no
  // visible confirmation anywhere in the UI - including the two ways it can
  // fail (the login email is not confirmed yet, the person already registered).
  const registration = location.state?.registration || null;
  const { language } = useLanguage();
  const registeredEventName = eventNameFor(intl.locale, location.state?.registeredEventName, location.state?.registeredEventNameEN)
    || (registration?.success ? eventNameFor(intl.locale, registration.eventName, registration.eventNameEN) : '');
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);
  const [profile, setProfile] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [shareEntry, setShareEntry] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState(null);
  // Set once, from whatever ?invite=<eventId> the page was opened with (see
  // SpeakerInvitationLanding.js's nextStep) - which engagement card to scroll to and outline so a speaker
  // arriving to decide on one invitation among several isn't left to find it
  // themselves. Read once rather than tracked live: the param is stripped
  // from the URL right away (same reasoning as the ?edit=1 handling below),
  // and the card itself stops needing the highlight once it has it.
  const [highlightEventId] = useState(() => searchParams.get('invite'));
  const cardRefs = useRef(new Map());
  // The speaker area's cards (hub/SpeakerArea.js hands them in through registerSpeakerCard) have refs
  // of their own: the event's open invitation there is the card the ?invite= link was sent for, and
  // it comes first in that section.
  const inviteRefs = useRef(new Map());
  const registerSpeakerCard = (eventId, el) => {
    const key = String(eventId);
    if (!inviteRefs.current.get(key)?.isConnected) inviteRefs.current.set(key, el);
  };

  // --- data layer: the new member API (/api/me/**), mapped onto the shape this page was built on ---
  //
  // The page is the pre-switch profile (attendee/AttendeeProfile.js) with its data source changed
  // only. The one old call with no new equivalent is the staged-draft resolve that provisioned the
  // account on first visit: the new model carries what an invitation proposes as change sets (D9),
  // which the person decides per field below instead of anything being applied on load.
  const toProfile = (me) => ({ ...(me.profile || {}), email: me.account?.email, emailVerified: me.account?.emailVerified });
  const toHistoryEntry = (h) => ({ ...h, id: `engagement-${h.engagementId}`, engagementId: h.engagementId, eventNameEN: h.eventNameEn, sortAt: h.createdAt, sortTie: -h.engagementId });
  // The old history listed every engagement, the speaking ones included; on the new model a speaking
  // role is a spot the person was asked about (GET /api/me/invitations). Every spot that is no longer
  // waiting for an answer becomes the old history row ("Speaker - Confirmed", "Chair / Moderator -
  // Declined", ...), with the talk as its summary, as the old speaker rows had. The history holds what
  // needs no managing (owner, 2026-10-20): an open spot is answered in the speaker area and a confirmed
  // spot of an upcoming event is managed there, so neither is a history row; a confirmed spot becomes
  // one once its event is over (and stays in the speaker area for its slides).
  const historyFromSpots = (groups) => (groups || []).flatMap((group) => (group.spots || [])
    .filter((spot) => spot.state !== 'OPEN' && group.event?.showInHistory !== false)
    .filter((spot) => spot.state !== 'CONFIRMED' || isPastDate(group.event?.dateTime))
    .map((spot) => spotRow(group, spot)));
  const splitTime = (time) => (time ? time.split('–') : []);
  const spotRow = (group, spot) => {
    const [start, end] = splitTime(spot.time);
    const [blockStart, blockEnd] = splitTime(spot.blockTime);
    const isTalk = spot.role === 'SPEAKER';
    return {
      id: `spot-${spot.assignmentId}`,
      assignmentId: spot.assignmentId,
      role: spot.role,
      status: spot.state,
      summary: isTalk ? spot.entryTitle : null,
      eventId: group.eventId,
      eventSlug: group.event?.slug,
      eventName: group.event?.name,
      eventNameEN: group.event?.nameEn,
      eventDateTime: group.event?.dateTime,
      eventLocation: group.event?.location,
      eventImageUrl: group.event?.imageUrl,
      // what the share card reads (ShareEngagementModal): the session and the talk's own slot
      sessionTitle: isTalk ? spot.blockTitle : (spot.entryTitle || spot.blockTitle),
      sessionStartTime: isTalk ? blockStart : start,
      sessionEndTime: isTalk ? blockEnd : end,
      talkStartTime: isTalk ? start : undefined,
      talkEndTime: isTalk ? end : undefined,
      sortAt: spot.askedAt,
      sortTie: spot.invitationId,
    };
  };

  const loadHistory = () => SelfApi.fetchHistory().then((rows) => setHistory((rows || []).map(toHistoryEntry))).catch(() => setHistory([]));

  // Proposed field changes sent with an invitation (D9): the new-model stand-in for the old staged
  // draft - nothing is applied on load, the person accepts or rejects each field.
  const [changeSets, setChangeSets] = useState([]);
  const [changeBusyField, setChangeBusyField] = useState(null);
  const [changeError, setChangeError] = useState(null);
  const changesRef = useRef(null);

  const decideChange = async (fieldId, action) => {
    setChangeBusyField(fieldId);
    setChangeError(null);
    try {
      if (action === 'accept') await SelfApi.acceptField(fieldId); else await SelfApi.rejectField(fieldId);
      const [me, sets] = await Promise.all([SelfApi.fetchMe(), SelfApi.fetchChangeSets()]);
      setProfile(toProfile(me));
      setChangeSets(sets || []);
    } catch (err) {
      setChangeError(failureText(err, t('self.profile.decisionError', 'Your decision could not be saved. Please try again.')));
    } finally {
      setChangeBusyField(null);
    }
  };
  const scrollToChanges = () => changesRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  // Every spot the person was asked about (D10): the open ones are answered per spot in the
  // invitation cards, the others are history rows.
  const [invitations, setInvitations] = useState([]);
  const reloadInvitations = () => SelfApi.fetchInvitations().then((rows) => setInvitations(rows || [])).catch(() => setInvitations([]));

  useEffect(() => {
    // One read per card list; the profile decides between the page and its error state, the three
    // lists simply stay empty when their own call fails, as today.
    SelfApi.fetchMe().then((me) => setProfile(toProfile(me))).catch(() => setProfile({ error: true }));
    loadHistory();
    SelfApi.fetchChangeSets().then((sets) => setChangeSets(sets || [])).catch(() => setChangeSets([]));
    SelfApi.fetchInvitations().then((rows) => setInvitations(rows || [])).catch(() => setInvitations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchParams.get('invite')) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('invite');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs again whenever the lists reload (not just on mount) - the card this
  // eventId belongs to doesn't exist in the DOM yet on first paint, before
  // the fetches above resolve. The speaker area's card comes first: its open
  // invitation is what the link was sent for (once answered, its confirmed
  // card); then the event's history row.
  useEffect(() => {
    if (!highlightEventId) return;
    const key = String(highlightEventId);
    const card = [inviteRefs.current.get(key), cardRefs.current.get(key)].find((el) => el?.isConnected);
    card?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [highlightEventId, history, invitations]);

  // /profile#referentenbereich (where /speaker/dashboard redirects): the router does not scroll to a
  // hash itself, and the section only exists once the invitations have loaded - so this waits for it,
  // and scrolls once. A jump rather than a smooth scroll: ScrollToTop's smooth scroll to the top of the
  // new page may still be running, and a jump ends it where a second smooth scroll can lose to it.
  const locationHash = location.hash;
  const speakerAreaRef = useRef(null);
  const scrolledToHash = useRef(false);
  useEffect(() => {
    if (scrolledToHash.current || locationHash !== `#${SPEAKER_AREA_ID}` || !speakerAreaRef.current) return;
    scrolledToHash.current = true;
    speakerAreaRef.current.scrollIntoView?.({ block: 'start' });
  }, [locationHash, invitations, profile]);
  const scrollToSpeakerArea = (e) => {
    e.preventDefault();
    speakerAreaRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const openEdit = () => { setDraft(profile); setMessage(null); setEditing(true); };

  // Deep-link entry into the edit form: the header's "Edit my profile" item
  // (layouts/Header.js) navigates to /profile?edit=1, and the modal opens here
  // once the profile it needs to seed the draft has loaded. Runs on
  // location.search rather than only on mount so clicking that item while
  // already on /profile works too; the flag is then removed with replace so
  // the URL settles back on a clean /profile that closing, refreshing or
  // going Back cannot reopen.
  useEffect(() => {
    if (searchParams.get('edit') !== '1' || !profile || profile.error) return;
    // Already editing (the item was clicked twice, or the pencil badge) -
    // keep the draft the user is working in rather than resetting it.
    if (!editing) {
      setDraft(profile);
      setMessage(null);
      setEditing(true);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      return next;
    }, { replace: true });
  }, [searchParams, profile, editing, setSearchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDraft({ ...draft, [name]: value });
  };

  const toggleDraftList = (key, value) => {
    setDraft((current) => ({
      ...current,
      [key]: (current[key] || []).includes(value)
        ? current[key].filter((v) => v !== value)
        : [...(current[key] || []), value],
    }));
  };

  const roleLabel = (status) => t(`speaker.role.${status}`, status.replaceAll('_', ' '));

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage(null);
    if ((draft.interests || []).length < MIN_INTERESTS) {
      setMessage({ text: t('speaker.onboarding.err.interests', 'Please select at least three cardiac interests.'), error: true });
      return;
    }
    setSaving(true);
    try {
      // Only the keys that moved while the form was open (a photo picked in the form included: the
      // upload only stored it, as before the switch); the member API answers the whole profile as
      // GET does.
      const me = await SelfApi.updateProfile(changedFields(profile, draft));
      setProfile(toProfile(me));
      setEditing(false);
    } catch (err) {
      console.error('Profile save failed:', err);
      setMessage({ text: failureText(err, t('profile.saveError', 'Could not save your profile. Please try again.')), error: true });
    } finally {
      setSaving(false);
    }
  };

  const handleResendVerification = async () => {
    setVerificationMessage(null);
    try {
      await SelfApi.resendVerificationEmail();
      setVerificationMessage(t('profile.verificationSent', 'Verification email sent - please check your inbox.'));
    } catch (err) {
      setVerificationMessage(t('profile.verificationError', 'Could not send verification email.'));
    }
  };

  const formatDate = (v) => (v ? new Date(v).toLocaleString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
  const initials = (fn, ln) => [fn, ln].filter(Boolean).map((w) => w[0]?.toUpperCase()).join('') || '·';
  const statusLabel = (s) => t(`profile.status.${s}`, s);
  const isPastEvent = isPastDate;
  const roleLabelFor = (role) => t(`profile.history.role.${role}`, role);
  // SUBMITTER carries the specific engagement id so SubmissionsPage can load
  // this exact submission into the edit form instead of the blank "new
  // submission" one - a person can have several submissions, so just
  // linking to /submissions with no id landed on the wrong (empty) state.
  // Moderator/panelist go through the exact same speaker invite/accept/
  // decline pipeline as SPEAKER (see SpeakerInvitationService.isSpeakerSideRole)
  // - every speaker-side role, not just SPEAKER, needs this link. It points at
  // the speaker area on this page (an in-page anchor, see renderCard), only
  // while that section is shown and only for a confirmed spot (the one kind
  // the section still manages; a declined one has nothing there).
  const isSpeakerSide = (role) => ['SPEAKER', 'MODERATOR', 'PANELIST'].includes(role);
  const showSpeakerArea = hasSpeakerSpots(invitations);
  const manageLink = (entry) => (isSpeakerSide(entry.role)
    ? (showSpeakerArea && entry.status === 'CONFIRMED' ? `#${SPEAKER_AREA_ID}` : null)
    : entry.role === 'SUBMITTER' ? `/submissions?edit=${entry.engagementId}` : null);
  const manageLinkLabel = (role) => t(`profile.history.manage.${role}`, 'Manage');
  // Any confirmed speaking/moderating/paneling row gets a share image - same
  // roles ShareRow gates on in hub/SpeakerArea.js.
  const isShareable = (entry) => entry.status === 'CONFIRMED' && ['SPEAKER', 'MODERATOR', 'PANELIST'].includes(entry.role);

  if (!profile) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loading', 'Loading your profile…')}</Empty></HeroInner></Hero></Page>;
  }

  if (profile.error) {
    return <Page><Hero><HeroInner><Empty>{t('profile.loadError', 'Your profile could not be loaded.')}</Empty></HeroInner></Hero></Page>;
  }

  // The account title is a name prefix (Mr./Ms./Dr./Prof. - the signup and
  // edit forms' title options), so it belongs on the name line under the
  // photo, not on the specialty line. The forms' explicit "no title" choices
  // store a literal sentinel ('No title'/'keine Angabe') which is never
  // rendered - same convention as UserManagement.js's fullName().
  const displayName = [profile.title, profile.firstName, profile.lastName]
    .filter((v) => v && v !== 'No title' && v !== 'keine Angabe')
    .join(' ') || profile.email;
  const titleLine = [profile.specialty, profile.institution].filter(Boolean).join(' · ');
  const motivation = (intl.locale || '').startsWith('de') ? (profile.motivationDe || profile.motivationEn) : (profile.motivationEn || profile.motivationDe);
  const needsCompletion = (profile.interests || []).length < MIN_INTERESTS || (profile.professionalStatuses || []).length === 0;
  // What the organizers proposed and the person has not decided yet (D9), split the way the old page
  // split its organizer-staged data: a field that already has a value of its own gets a suggestion,
  // a blank one was "filled in" by the organizers. Only the organizers' proposals count (ADMIN,
  // IMPORT and RESEARCH change sets), as the old banner spoke only of organizer-staged data.
  const isBlank = (value) => value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  const waitingFields = (changeSets || [])
    .filter((set) => ['ADMIN', 'IMPORT', 'RESEARCH'].includes(set.origin))
    .flatMap((set) => set.fields || [])
    .filter((field) => field.state === 'WAITING');
  const suggestedCount = waitingFields.filter((field) => !isBlank(field.current)).length;
  const filledCount = waitingFields.filter((field) => isBlank(field.current)).length;
  // Old and new rows in one list, newest first, as the old history was ordered by creation. Rows
  // with the same time keep the old list's order: registrations in the order they were written
  // (ascending id, as the old query returned them), the spots of one invitation the later one first.
  const newestFirst = (a, b) => String(b.sortAt || '').slice(0, 19).localeCompare(String(a.sortAt || '').slice(0, 19))
    || (b.sortTie || 0) - (a.sortTie || 0);
  const historyRows = [...history, ...historyFromSpots(invitations)].sort(newestFirst);

  // One card of the old history list. Open invitations are not among them: they are answered in
  // the speaker area (hub/SpeakerArea.js).
  const renderCard = (entry) => {
    const link = manageLink(entry);
    const past = isPastEvent(entry.eventDateTime);
    const highlighted = highlightEventId && String(entry.eventId) === String(highlightEventId);
    return (
      <RegCard key={entry.id} $past={past} $highlighted={highlighted}
        ref={el => {
          // the event's first card in the history
          const key = String(entry.eventId);
          if (el && !cardRefs.current.get(key)?.isConnected) cardRefs.current.set(key, el);
        }}>
        <CardTop>
          {entry.eventSlug ? (
            <ThumbLink to={`/events/${entry.eventSlug}`}><EventThumb url={entry.eventImageUrl} /></ThumbLink>
          ) : (
            <EventThumb url={entry.eventImageUrl} />
          )}
          <RegInfo>
            <RegName>
              <RoleTag>{roleLabelFor(entry.role)}</RoleTag>
              {entry.eventSlug ? (
                <EventNameLink to={`/events/${entry.eventSlug}`}>{eventNameFor(intl.locale, entry.eventName, entry.eventNameEN)}</EventNameLink>
              ) : eventNameFor(intl.locale, entry.eventName, entry.eventNameEN)}
            </RegName>
            <RegMeta>{[formatDate(entry.eventDateTime), entry.eventLocation].filter(Boolean).join(' · ')}</RegMeta>
            <StatusTag $status={entry.status}>{statusLabel(entry.status)}</StatusTag>
            {past && <PastTag>{t('profile.history.past', 'Past')}</PastTag>}
            {entry.summary && <RegSummary>“{entry.summary}”</RegSummary>}
            {link && <div>{link.startsWith('#')
              ? <RegAnchor href={link} onClick={scrollToSpeakerArea}>{manageLinkLabel(entry.role)} →</RegAnchor>
              : <RegLink to={link}>{manageLinkLabel(entry.role)} →</RegLink>}</div>}
          </RegInfo>
          {isShareable(entry) && (
            <ShareIconButton
              type="button"
              title={t('profile.history.share', 'Share this engagement')}
              aria-label={t('profile.history.share', 'Share this engagement')}
              onClick={() => setShareEntry({ ...entry, talkTitle: entry.summary })}
            >
              <Share2 size={15} />
            </ShareIconButton>
          )}
        </CardTop>
      </RegCard>
    );
  };

  return (
    <Page>
      <Hero>
        <HeroInner>
          {/* Hardcoded (non-i18n) header copy for the universal profile page -
              requested verbatim by the customer. */}
          <BigHeadline>YOUR PORT TO THE ART OF MEDICAL SCIENCE</BigHeadline>
          <SubHeadline>Build your best CARE with HEARTPORT.</SubHeadline>
          <TagLine>Join HEARTS conferences. Connect with experts. Keep up with research. Share your medical art. Apply AI for clinical excellence: DEMOCRATIZING SCIENCE.</TagLine>
        </HeroInner>
      </Hero>

      <Grey>
        <GreyInner $withSpeaker={showSpeakerArea}>
          <TopLeft>
            {registeredEventName && (
              <RegisteredBanner>
                {t('verify.eventRegistered', "You're now registered for {eventName}.", { eventName: registeredEventName })}
              </RegisteredBanner>
            )}

            {registration && !registration.success && registration.error?.error === 'EMAIL_NOT_VERIFIED' && (
              <Notice>
                {t('profile.registrationNeedsVerification', 'Your registration is not complete yet. Please confirm your email address, then register for the event again.')}
              </Notice>
            )}
            {registration && !registration.success && registration.status === 409 && (
              <Notice>
                {t('profile.registrationAlreadyRegistered', 'You are already registered for this event.')}
              </Notice>
            )}

            {!profile.emailVerified && (
              <VerifyNotice>
                <span>{t('profile.verifyNotice', 'Please verify your email to register for events.')}</span>
                <VerifyLink type="button" onClick={handleResendVerification}>
                  {t('profile.resendVerification', 'Resend verification email')}
                </VerifyLink>
              </VerifyNotice>
            )}
            {verificationMessage && <Message>{verificationMessage}</Message>}

            {(suggestedCount > 0 || filledCount > 0) && (
              <Nudge>
                <span>
                  {suggestedCount > 0
                    ? t('profile.organizerSuggestions', 'The organizers suggested updates to {count, plural, one {# field} other {# fields}} of your profile.', { count: suggestedCount })
                    : t('profile.organizerFilled', 'The organizers filled in parts of your profile for you.')}
                </span>
                <NudgeLink type="button" onClick={scrollToChanges}>
                  {suggestedCount > 0
                    ? t('profile.organizerSuggestionsAction', 'Review suggestions')
                    : t('profile.organizerFilledAction', 'Take a look')}
                </NudgeLink>
              </Nudge>
            )}

            {needsCompletion && (
              <Nudge>
                <span>{t('profile.completeNudge', 'Add your topic interests so we can match you to the right sessions and panels.')}</span>
                <NudgeLink type="button" onClick={openEdit}>{t('profile.completeNudgeAction', 'Complete my profile')}</NudgeLink>
              </Nudge>
            )}

            {/* What an invitation proposed, decided per field (D9) - the new-model stand-in for the
                staged draft the old screen resolved on load. Placed in the old page's "things to look
                at" column, right under the nudges that announce it. */}
            <div ref={changesRef}>
              <ProposedChanges changeSets={changeSets} busyField={changeBusyField} error={changeError} onDecide={decideChange} />
            </div>

            {motivation && (
              <>
                <MotivationLabel>{t('profile.motivationLabel', 'I am part of HEARTS - DEMOCRATIZING SCIENCE - because')}</MotivationLabel>
                <MotivationText>{motivation}</MotivationText>
              </>
            )}
          </TopLeft>

          <TopRight>
            <PhotoWrap>
              <PhotoButton type="button" onClick={openEdit} aria-label={t('profile.editProfile', 'Edit my profile')}>
                {profile.photoUrl ? <Photo src={mediaUrl(profile.photoUrl)} alt="" referrerPolicy="no-referrer" /> : <Initials>{initials(profile.firstName, profile.lastName)}</Initials>}
                <EditBadge className="edit-badge"><Pencil size={16} /></EditBadge>
              </PhotoButton>
              <Name>{displayName}</Name>
              {titleLine && <TitleLine>{titleLine}</TitleLine>}
            </PhotoWrap>
          </TopRight>

          {/* The speaker area, at the top of the profile: next to the photo on a desktop, right under
              it on a phone. The one place for everything about speaking - open invitations answered
              inline, confirmed spots with their talk tools. Only for a person who was asked to a spot
              (any state) - a plain attendee has no such section. */}
          {showSpeakerArea && (
            <SpeakerSection id={SPEAKER_AREA_ID} ref={speakerAreaRef} aria-labelledby={`${SPEAKER_AREA_ID}-title`}>
              <HistoryHeader>
                <HistoryTitle id={`${SPEAKER_AREA_ID}-title`}>{t('profile.speakerArea.title', 'Speaker area')}</HistoryTitle>
                <HistorySubtitle>{t('profile.speakerArea.subtitle', 'Your invitations, talks, slides and engagements as a speaker, moderator or panelist')}</HistorySubtitle>
              </HistoryHeader>
              <SpeakerArea
                invitations={invitations}
                identity={{ name: displayName, institution: profile.institution, city: profile.city, country: profile.country, photoUrl: profile.photoUrl, motivation }}
                onAnswered={reloadInvitations}
                highlightEventId={highlightEventId}
                registerCard={registerSpeakerCard}
              />
            </SpeakerSection>
          )}

          <BottomLeft>
            <RecommendedStudies t={t} />
          </BottomLeft>

          <BottomRight>
            {/* Participations that need no managing: registrations, attendance, answered and past
                speaking. Open invitations and upcoming confirmed spots are the speaker area's. */}
            <HistoryHeader>
              {/* Hardcoded like RecommendedStudies.js's own banner - not routed through react-intl. */}
              <HistoryTitle>HEARTS Conferences</HistoryTitle>
              <HistorySubtitle>Manage upcoming live conferences</HistorySubtitle>
            </HistoryHeader>
            {/* The old list held the invited rows too, so it never said "not registered" while an
                invitation waited; neither does this one while the speaker area has anything in it. */}
            {historyRows.length === 0 ? (
              !showSpeakerArea && <Empty>{t('profile.noEvents', 'You are not registered for any events yet.')}</Empty>
            ) : (
              historyRows.map((entry) => renderCard(entry))
            )}
          </BottomRight>
        </GreyInner>
      </Grey>

      {editing && draft && (
        <ProfileEditModal
          draft={draft} countryOptions={countryOptions} message={message} saving={saving}
          t={t} roleLabel={roleLabel}
          onChange={handleChange} onToggleList={toggleDraftList}
          onInterestsChange={(v) => setDraft((current) => ({ ...current, interests: v }))}
          onSubmit={handleSave} onCancel={() => setEditing(false)}
          fieldDiffs={[]}
        />
      )}

      {shareEntry && (
        <ShareEngagementModal
          engagement={shareEntry}
          identity={{ name: displayName, institution: profile.institution, city: profile.city, country: profile.country, photoUrl: profile.photoUrl, motivation }}
          onClose={() => setShareEntry(null)}
        />
      )}
    </Page>
  );
};

export default AttendeeProfile;
